// ** React Imports
import { useState, Fragment, useEffect, useContext } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Backdrop from '@mui/material/Backdrop'
import MuiPopover from '@mui/material/Popover'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import CircularProgress from '@mui/material/CircularProgress'

import useWebSocket from 'src/hooks/useWebSocket'
import Icon from 'src/@core/components/icon'
import PerfectScrollbarComponent from 'react-perfect-scrollbar'

import { notificationSocketUrl } from 'src/configs/api'
import authConfig from 'src/configs/auth'
import { AuthContext } from 'src/context/AuthContext'
import { authStorage } from 'src/utils/authStorage'

// ** Styled Components

const NotificationPopover = styled(MuiPopover)(({ theme }) => ({
  zIndex: '1300 !important',
  '& .MuiPopover-paper': {
    width: 380,
    borderRadius: '12px',
    marginTop: theme.spacing(0),
    overflow: 'visible',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    borderTop: `0px solid ${theme.palette.primary.main}`,
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100vw - 32px)',
      maxWidth: 380
    },
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 14,
      width: 12,
      height: 12,
      background: '#1B205C',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 1
    }
  }
}))

const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  maxHeight: 360
})

const NotificationItemRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive'
})(({ theme, isActive }) => ({
  padding: theme.spacing(3, 4),
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: isActive
    ? theme.palette.mode === 'dark'
      ? 'rgba(43,56,143,0.10)'
      : 'rgba(43,56,143,0.05)'
    : 'transparent',
  '&:hover': {
    backgroundColor: isActive
      ? theme.palette.mode === 'dark'
        ? 'rgba(43,56,143,0.12)'
        : 'rgba(43,56,143,0.07)'
      : theme.palette.action.hover
  },
  '&:last-of-type': {
    borderBottom: 'none'
  }
}))

const UnreadDot = styled(Box)(() => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#52d78f',
  flexShrink: 0,
  marginTop: 5
}))

const NotificationContentPanel = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : theme.palette.grey[100],
  borderRadius: 6,
  padding: theme.spacing(1.5, 2.5),
  marginTop: theme.spacing(1.5),
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  color: theme.palette.text.secondary,
  maxHeight: 120,
  overflowY: 'auto',
  wordBreak: 'break-word',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: theme.palette.divider
  }
}))

const ScrollWrapper = ({ children, hidden }) => {
  if (hidden) {
    return (
      <Box sx={{ maxHeight: 360, overflowY: 'auto', overflowX: 'hidden' }}>
        {children}
      </Box>
    )
  }

  return (
    <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>
      {children}
    </PerfectScrollbar>
  )
}

const NotificationDropdown = props => {
  const storedToken = authStorage.getStoredToken(authConfig.storageTokenKeyName)
  const wsUrl = `${notificationSocketUrl}?authToken=${storedToken}`

  const { fetchNotificationContent, markAsRead, markAllAsRead } = useWebSocket(wsUrl)
  const { socketData, notificationMessage, setNotificationMessage } = useContext(AuthContext)

  const [notificationDropdown, setNotificationDropDown] = useState([])
  const [activeNotificationId, setActiveNotificationId] = useState(null)
  const [loadingId, setLoadingId] = useState(null)

  const { settings } = props
  const [anchorEl, setAnchorEl] = useState(null)
  const hidden = useMediaQuery(theme => theme.breakpoints.down('lg'))
  const { direction } = settings

  useEffect(() => {
    if (socketData) setNotificationDropDown(socketData)
  }, [socketData])

  useEffect(() => {
    if (!anchorEl) {
      setActiveNotificationId(null)
      setNotificationMessage?.(null)
    }
  }, [anchorEl])

  const handleDropdownOpen = event => setAnchorEl(event.currentTarget)

  const handleDropdownClose = () => {
    setAnchorEl(null)
    setActiveNotificationId(null)
    setNotificationMessage?.(null)
  }

  const isUnread = notification =>
    !notification.messageReadStatus || notification.messageReadStatus === 'unread'

  const openNotification = async id => {
    if (activeNotificationId === id) {
      setActiveNotificationId(null)
      setNotificationMessage?.(null)
      return
    }

    setActiveNotificationId(id)
    setNotificationMessage?.(null)
    setLoadingId(id)

    const notification = notificationDropdown.find(n => n.notificationId === id)
    await fetchNotificationContent(id)

    if (notification && isUnread(notification)) markAsRead(id)
    setLoadingId(null)
  }

  const unreadCount = notificationDropdown.filter(n => isUnread(n)).length
  const open = Boolean(anchorEl)

  return (
    <Fragment>
      <IconButton
        color='inherit'
        aria-haspopup='true'
        onClick={handleDropdownOpen}
        aria-controls='notification-popover'
      >
        <Badge
          color='error'
          variant='dot'
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              top: 4,
              right: 4,
              boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}`
            }
          }}
        >
          <Icon icon='mdi:bell-outline' />
        </Badge>
      </IconButton>

      <Backdrop open={open} onClick={handleDropdownClose} sx={{ zIndex: 1200, backgroundColor: 'rgba(0,0,0,0.8)' }} />

      <NotificationPopover
        id='notification-popover'
        open={open}
        anchorEl={anchorEl}
        onClose={handleDropdownClose}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: direction === 'ltr' ? 'right' : 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: direction === 'ltr' ? 'right' : 'left'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 4,
            py: 3,
            position: 'relative',
            zIndex: 1,
            borderRadius: '12px 12px 0 0',
            background: '#1B205C'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Icon icon='mdi:bell-outline' fontSize={20} style={{ color: 'white' }} />
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'common.white' }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Box
                sx={{
                  minWidth: 20,
                  height: 20,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  color: 'common.white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  px: 1
                }}
              >
                {unreadCount}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {unreadCount > 0 && (
              <Button
                size='small'
                variant='text'
                onClick={markAllAsRead}
                startIcon={
                  <Box
                    sx={{
                      width: 17,
                      height: 17,
                      borderRadius: '50%',
                      border: '1.5px solid rgba(255,255,255,0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Icon icon='mdi:check' fontSize={11} />
                  </Box>
                }
                sx={{
                  fontSize: '0.72rem',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1.25,
                  py: 0.5,
                  minWidth: 0,
                  textTransform: 'none',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  lineHeight: 1,
                  transition: 'background-color 0.18s ease',
                  '& .MuiButton-startIcon': { mr: 0.5 },
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.28)', color: '#fff', textDecoration: 'underline' }
                }}
              >
                Mark all read
              </Button>
            )}
            <IconButton
              size='small'
              onClick={handleDropdownClose}
              sx={{
                color: 'rgba(255,255,255,0.85)',
                p: 0.5,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', color: 'common.white' }
              }}
            >
              <Icon icon='mdi:close' fontSize={18} />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'divider' }} />

        {/* Notification list */}
        <ScrollWrapper hidden={hidden}>
          {notificationDropdown && notificationDropdown.length > 0 ? (
            notificationDropdown.map((notification, index) => {
              const isActive = activeNotificationId === notification.notificationId
              const isLoading = loadingId === notification.notificationId
              const unread = isUnread(notification)

              return (
                <NotificationItemRow
                  key={notification.notificationId || index}
                  isActive={isActive}
                  onClick={() => openNotification(notification.notificationId)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                    {/* Unread indicator column */}
                    <Box sx={{ width: 8, flexShrink: 0 }}>
                      {unread && <UnreadDot sx={{ backgroundColor: isActive ? '#9e9e9e' : '#52d78f' }} />}
                    </Box>

                    {/* Subject + chevron */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                          variant='body2'
                          sx={{
                            flex: 1,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            color: unread ? 'primary.main' : 'text.primary',
                            fontWeight: unread ? 600 : 400,
                            transition: 'color 0.2s ease'
                          }}
                        >
                          {notification.subject}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.disabled',
                            flexShrink: 0,
                            transition: 'transform 0.2s ease',
                            transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        >
                          <Icon icon='mdi:chevron-down' fontSize={16} />
                        </Box>
                      </Box>

                      {/* Expanded message content */}
                      <Collapse in={isActive} timeout={200} unmountOnExit>
                        {isLoading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              mt: 2,
                              color: 'text.disabled'
                            }}
                          >
                            <CircularProgress size={14} color='inherit' />
                            <Typography variant='caption'>Loading message…</Typography>
                          </Box>
                        ) : notificationMessage ? (
                          <NotificationContentPanel>
                            {typeof notificationMessage === 'string' && notificationMessage}
                            {typeof notificationMessage === 'object' && (
                              <>
                                {notificationMessage.subject && (
                                  <Typography
                                    variant='caption'
                                    sx={{
                                      fontWeight: 600,
                                      display: 'block',
                                      mb: 0.5,
                                      color: 'text.secondary'
                                    }}
                                  >
                                    {notificationMessage.subject}
                                  </Typography>
                                )}
                                {notificationMessage.body ||
                                  notificationMessage.message ||
                                  notificationMessage.content ||
                                  JSON.stringify(notificationMessage)}
                              </>
                            )}
                          </NotificationContentPanel>
                        ) : (
                          <Box sx={{ mt: 2 }}>
                            <Typography
                              variant='caption'
                              sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                            >
                              No message content available.
                            </Typography>
                          </Box>
                        )}
                      </Collapse>
                    </Box>
                  </Box>
                </NotificationItemRow>
              )
            })
          ) : (
            <Box
              sx={{
                py: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                color: 'text.disabled'
              }}
            >
              <Icon icon='mdi:bell-off-outline' fontSize={36} />
              <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                No new notifications
              </Typography>
            </Box>
          )}
        </ScrollWrapper>
      </NotificationPopover>
    </Fragment>
  )
}

export default NotificationDropdown
